import { useState } from 'react';
import { useParams, Link, Navigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiAuthError } from '../lib/api';
import { Button, Badge, Spinner } from '../atoms';
import {
  VERDICT_LABELS, BUILD_STATE_LABELS, sendLabel,
  type ReviewState, type ReviewItem, type ReviewSummary, type Verdict, type BuildRequest,
} from '../lib/buildMode';

/** Strips the api client's "HTTP NNN:" prefix so the reviewer sees a sentence. */
function errText(error: unknown, fallback: string): string {
  const raw = error instanceof Error ? error.message : '';
  return (raw || fallback).replace(/^HTTP \d+:\s*/, '');
}

const truthy = (v: boolean | number | null | undefined) => v === true || v === 1;

/**
 * Second gate on a deep link. The server already validates the scheme on write, so this
 * is defense in depth rather than the primary control — but an `<a href>` is an XSS sink
 * and React 18 does NOT block `javascript:` there; it warns and renders. A row written
 * before the server-side check, or by any future path that forgets it, would otherwise be
 * one click from executing, on a page whose whole instruction to the reader is "open it".
 *
 * Validating in exactly one place is the pattern this codebase keeps getting bitten by.
 */
function safeHref(raw: string | null): string | null {
  const v = (raw ?? '').trim();
  if (!v || v.startsWith('//')) return null;
  if (v.startsWith('/')) return v;
  return /^https?:\/\//i.test(v) ? v : null;
}

/**
 * BuildReview — /build/:id/review
 *
 * The page that makes the loop real: the customer walks the build, renders a verdict per
 * check, and presses ONE button whose label already says what it will do.
 *
 * THE TWO AXES ARE INDEPENDENT AND BOTH ARE NEEDED.
 *   `built`   — does the work exist? Our claim, which the reviewer confirms.
 *   `verdict` — is it right? Their call, and the only one that gates the send.
 * Collapsing them would let "we built it" read as "they accepted it".
 *
 * PENDING IS EXPLICIT AND IT BLOCKS. It is a selectable state, stored, and counted —
 * never the absence of a value. An unwalked screen must not ride along on silence, which
 * is the same doctrine as a stated default on an open question: absence of a verdict is
 * not a verdict.
 *
 * THE SEND BUTTON IS THE DECISION. There is one, and its effect is DERIVED from the
 * verdicts — all pass ships, any fail sends it back. The client never chooses the
 * outcome, because a client-chosen outcome is a way to ship a failing round. And a fail
 * does not move the stage on its own: a reviewer three checks into five never has the
 * build yanked out from under them by their own first complaint.
 *
 * The counter strip and the button read the SAME server-derived summary, so "why is send
 * greyed out" is never a mystery.
 */
export function BuildReview() {
  const { id = '' } = useParams();
  const location = useLocation();
  const qc = useQueryClient();
  const [noteDraft, setNoteDraft] = useState<Record<string, string>>({});

  const build = useQuery({
    queryKey: ['build-mode', 'request', id],
    queryFn: () => api.get<BuildRequest>(`/api/build-mode/requests/${id}`),
    enabled: Boolean(id),
  });

  const review = useQuery({
    queryKey: ['build-mode', 'round', id],
    queryFn: () => api.get<ReviewState>(`/api/build-mode/requests/${id}/round`),
    enabled: Boolean(id),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['build-mode', 'round', id] });
    qc.invalidateQueries({ queryKey: ['build-mode', 'request', id] });
  };

  const [failedItem, setFailedItem] = useState<{ id: string; message: string } | null>(null);

  const setItem = useMutation({
    mutationFn: (vars: { itemId: string; verdict?: Verdict; built?: boolean; note?: string }) =>
      api.put<ReviewState>(`/api/build-mode/requests/${id}/round/items/${vars.itemId}`, {
        verdict: vars.verdict, built: vars.built, note: vars.note,
      }),
    onMutate: () => setFailedItem(null),
    onSuccess: () => { setFailedItem(null); invalidate(); },
    // A rejected save used to render NOTHING. The reviewer clicked Fail, the control did
    // not take, and there was no explanation — so the likeliest reading was "the app is
    // broken", and the dangerous one was "I recorded a fail" when the round still said
    // pending and the send stayed blocked for no visible reason.
    onError: (err, vars) => setFailedItem({ id: vars.itemId, message: errText(err, 'Could not save that.') }),
  });

  const submit = useMutation({
    mutationFn: () => api.post(`/api/build-mode/requests/${id}/round/submit`, {}),
    onSuccess: invalidate,
  });

  if (review.isLoading || build.isLoading) return <Spinner />;

  // An expired session is the COMMON failure here, not an exotic one: this page is a
  // link someone opens hours after it was sent. Send them to sign in rather than
  // rendering a raw "Not authenticated" string they can do nothing with.
  if (review.error instanceof ApiAuthError || build.error instanceof ApiAuthError) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  }

  if (review.isError) {
    return <p style={{ color: 'var(--color-status-red)' }}>{errText(review.error, 'Could not load this review.')}</p>;
  }

  // NOT `review.data as ReviewState`. That cast asserted to the compiler what was not
  // true at runtime — when the query settles without data (an auth failure being the
  // ordinary way), `state.summary` threw and the top-level error boundary took down the
  // WHOLE page with "Something went wrong. Please reload." This is the same defect class
  // CLAUDE.md already records from the parcel Tax Records tab: an `as` cast standing in
  // for a guard, so the crash lands nowhere near its cause.
  const state = review.data;
  if (!state) {
    return <p style={{ color: 'var(--color-muted)' }}>Could not load this review. Try reloading.</p>;
  }
  const summary = state.summary;

  if (!state.round) {
    return (
      <section style={{ maxWidth: 860 }}>
        <Link to="/build">← Back to Build Mode</Link>
        <h1 style={{ fontSize: 'var(--fs-xl)' }}>Nothing to review yet</h1>
        <p style={{ color: 'var(--color-muted)' }}>
          A review opens when this build reaches preview.
        </p>
      </section>
    );
  }

  const sections: Array<{ key: ReviewItem['section']; heading: string; blurb: string }> = [
    { key: 'walk', heading: 'Walk it yourself', blurb: 'Each check links straight to the thing it describes.' },
    { key: 'assumption', heading: 'What we built on', blurb: 'The assumptions this build rests on. If one is wrong, the build is wrong.' },
    { key: 'reference', heading: 'Reference', blurb: 'Context — what was touched, and what was deliberately left alone.' },
  ];

  return (
    <section style={{ maxWidth: 900, display: 'grid', gap: 'var(--sp-4)' }}>
      <div>
        <Link to="/build">← Back to Build Mode</Link>
        <h1 style={{ fontSize: 'var(--fs-xl)', margin: 'var(--sp-2) 0 0' }}>
          Review round {state.round.round_number}
        </h1>
        {build.data?.request_text && (
          <p style={{ color: 'var(--color-muted)', margin: 'var(--sp-1) 0 0' }}>{build.data.request_text}</p>
        )}
      </div>

      {build.data?.review_note && (
        <div style={{ padding: 'var(--sp-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
          <h2 style={{ fontSize: 'var(--fs-md)', margin: '0 0 var(--sp-1)' }}>What building it surfaced</h2>
          <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{build.data.review_note}</p>
        </div>
      )}

      <ProgressStrip summary={summary} />

      {sections.map(({ key, heading, blurb }) => {
        const items = state.items.filter((i) => i.section === key);
        if (items.length === 0) return null;
        return (
          <div key={key}>
            <h2 style={{ fontSize: 'var(--fs-lg)', margin: '0 0 var(--sp-1)' }}>{heading}</h2>
            <p style={{ color: 'var(--color-muted)', margin: '0 0 var(--sp-2)', fontSize: 'var(--fs-sm)' }}>{blurb}</p>
            <div style={{ display: 'grid', gap: 'var(--sp-2)' }}>
              {items.map((item) => (
                <ItemRow
                  key={item.id}
                  item={item}
                  noteDraft={noteDraft[item.id] ?? item.note ?? ''}
                  onNoteChange={(v) => setNoteDraft((d) => ({ ...d, [item.id]: v }))}
                  onSave={(patch) => setItem.mutate({ itemId: item.id, ...patch })}
                  pending={setItem.isPending}
                  error={failedItem && failedItem.id === item.id ? failedItem.message : null}
                />
              ))}
            </div>
          </div>
        );
      })}

      <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 'var(--sp-3)' }}>
        <Button
          variant="primary"
          disabled={!summary.can_send || submit.isPending}
          onClick={() => submit.mutate()}
        >
          {sendLabel(summary)}
        </Button>
        <p style={{ color: 'var(--color-muted)', fontSize: 'var(--fs-sm)', margin: 'var(--sp-1) 0 0' }}>
          {summary.can_send
            ? summary.outcome === 'ship'
              ? 'Every check passed. Sending ships this build and locks the round.'
              : 'Sending returns this build to us with your notes, and locks the round.'
            : 'Every check needs a verdict before this round can be sent. Pending is not passing.'}
        </p>
        {submit.isError && (
          <p style={{ color: 'var(--color-status-red)', fontSize: 'var(--fs-sm)' }}>
            {errText(submit.error, 'Could not send this round.')}
          </p>
        )}
      </div>
    </section>
  );
}

/**
 * The counter strip. It renders the server's summary verbatim — including the excluded
 * count, because an item shown but silently uncounted makes the progress bar lie.
 */
function ProgressStrip({ summary }: { summary: ReviewSummary }) {
  const done = summary.total - summary.pending;
  const pct = summary.total === 0 ? 0 : Math.round((done / summary.total) * 100);
  return (
    <div style={{ padding: 'var(--sp-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
      <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap', alignItems: 'center' }}>
        <strong>{summary.built}/{summary.total} built</strong>
        <Badge variant="success">{summary.pass} pass</Badge>
        <Badge variant="danger">{summary.fail} fail</Badge>
        <Badge variant="neutral">{summary.pending} pending</Badge>
      </div>
      <div
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        style={{ height: 6, background: 'var(--color-border)', borderRadius: 999, margin: 'var(--sp-2) 0 0' }}
      >
        <div style={{ width: `${pct}%`, height: '100%', background: 'var(--brand-primary)', borderRadius: 999 }} />
      </div>
      {summary.excluded > 0 && (
        <p style={{ color: 'var(--color-muted)', fontSize: 'var(--fs-sm)', margin: 'var(--sp-1) 0 0' }}>
          ({summary.excluded} item{summary.excluded === 1 ? '' : 's'} not counted as {summary.excluded === 1 ? 'a QA target' : 'QA targets'})
        </p>
      )}
    </div>
  );
}

/** One reviewable check: two independent axes, a note, and the context to judge it. */
function ItemRow({
  item, noteDraft, onNoteChange, onSave, pending, error,
}: {
  item: ReviewItem;
  noteDraft: string;
  onNoteChange: (v: string) => void;
  onSave: (patch: { verdict?: Verdict; built?: boolean; note?: string }) => void;
  pending: boolean;
  error: string | null;
}) {
  const verdicts: Verdict[] = ['pass', 'fail', 'pending'];
  return (
    <div style={{ padding: 'var(--sp-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-2)', alignItems: 'start' }}>
        <div>
          <label style={{ display: 'flex', gap: 'var(--sp-1)', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={truthy(item.built)}
              disabled={pending}
              onChange={(e) => onSave({ built: e.target.checked })}
            />
            <strong>{item.title}</strong>
          </label>
          {safeHref(item.deep_link) && (
            <a href={safeHref(item.deep_link) as string} style={{ fontSize: 'var(--fs-sm)' }}>Open it →</a>
          )}
        </div>
        <Badge variant="info">{BUILD_STATE_LABELS[item.build_state] ?? item.build_state}</Badge>
      </div>

      {(item.current_state || item.plan) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-2)', margin: 'var(--sp-2) 0' }}>
          <div>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-muted)' }}>CURRENT STATE</div>
            <div>{item.current_state || '—'}</div>
          </div>
          <div>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-muted)' }}>PLAN</div>
            <div>{item.plan || '—'}</div>
          </div>
        </div>
      )}

      {truthy(item.is_qa_target) ? (
        <div style={{ display: 'flex', gap: 'var(--sp-1)', margin: 'var(--sp-2) 0 0' }} role="group" aria-label="Verdict">
          {verdicts.map((v) => (
            <Button
              key={v}
              size="sm"
              variant={item.verdict === v ? 'primary' : 'ghost'}
              disabled={pending}
              aria-pressed={item.verdict === v}
              onClick={() => onSave({ verdict: v, note: noteDraft })}
            >
              {VERDICT_LABELS[v]}
            </Button>
          ))}
        </div>
      ) : (
        <p style={{ color: 'var(--color-muted)', fontSize: 'var(--fs-sm)', margin: 'var(--sp-2) 0 0' }}>
          Not a QA target — shown for context, not counted.
        </p>
      )}

      <textarea
        value={noteDraft}
        placeholder={item.verdict === 'fail' ? 'Required — say what is wrong' : 'QA notes / issues…'}
        onChange={(e) => onNoteChange(e.target.value)}
        onBlur={() => { if (noteDraft !== (item.note ?? '')) onSave({ note: noteDraft }); }}
        style={{ width: '100%', marginTop: 'var(--sp-2)', minHeight: 56 }}
      />

      {error && (
        <p role="alert" style={{ color: 'var(--color-status-red)', fontSize: 'var(--fs-sm)', margin: 'var(--sp-1) 0 0' }}>
          {error}
        </p>
      )}
    </div>
  );
}
